// Fechas de la prueba gratuita (`trial_ends_at`) en HORA CHILE (#1133).
//
// El backend guarda un instante en UTC (TIME_ZONE = "UTC"), pero el admin
// piensa en días del calendario chileno: "que le dure hasta el 8 de octubre".
// Si el control trabajara con la zona del navegador, un admin conectado desde
// otro huso movería el corte un día sin darse cuenta. Por eso todo pasa por
// America/Santiago de forma explícita, y lo que viaja al PATCH es un ISO con
// la Z, nunca un datetime ingenuo que el backend leería como UTC.

export const ZONA_CHILE = "America/Santiago";

// Hora a la que corta una prueba que no tenía fecha: la misma que usa
// `iniciar_demos` en el backend (spec 1133, R14).
export const HORA_CORTE_DEFAULT = "09:00";

const partes = (fecha) => {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: ZONA_CHILE,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const out = {};
  fmt.formatToParts(fecha).forEach((p) => {
    if (p.type !== "literal") out[p.type] = Number(p.value);
  });
  return out;
};

const pad = (n) => String(n).padStart(2, "0");

// "YYYY-MM-DD" del día chileno en que cae el instante.
export const fechaChile = (instante) => {
  const p = partes(new Date(instante));
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
};

// "HH:MM" en hora chilena del instante.
export const horaChile = (instante) => {
  const p = partes(new Date(instante));
  return `${pad(p.hour)}:${pad(p.minute)}`;
};

export const hoyChile = (ahora = Date.now()) => fechaChile(ahora);

// Suma días de calendario a un "YYYY-MM-DD". Aritmética en UTC puro para que
// un cambio de horario de verano no se coma ni duplique un día.
export const sumarDias = (ymd, dias) => {
  const [y, m, d] = ymd.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + dias));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
};

// Minutos que Chile está corrido de UTC en ese instante (-180 o -240).
const offsetChileMin = (ms) => {
  const p = partes(new Date(ms));
  const comoUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((comoUtc - ms) / 60000);
};

// El instante (ISO UTC) que corresponde a `ymd` a las `hhmm` en Chile. Se
// recalcula el offset una segunda vez porque el primero se mide sobre una
// estimación que puede caer del otro lado de un cambio de horario.
export const instanteChile = (ymd, hhmm = HORA_CORTE_DEFAULT) => {
  const [y, m, d] = ymd.split("-").map(Number);
  const [h, mi] = hhmm.split(":").map(Number);
  const estimado = Date.UTC(y, m - 1, d, h, mi);
  let utc = estimado - offsetChileMin(estimado) * 60000;
  const corregido = estimado - offsetChileMin(utc) * 60000;
  if (corregido !== utc) utc = corregido;
  return new Date(utc).toISOString();
};

// "8 de octubre" — sin año cuando es el año en curso, con año si no.
export const fechaLarga = (ymd, ahora = Date.now()) => {
  const [y] = ymd.split("-").map(Number);
  const mismoAnio = y === Number(hoyChile(ahora).slice(0, 4));
  return new Date(instanteChile(ymd, "12:00")).toLocaleDateString("es-CL", {
    timeZone: ZONA_CHILE,
    day: "numeric",
    month: "long",
    ...(mismoAnio ? {} : { year: "numeric" }),
  });
};
